"use client";
import { useEffect, useState } from "react";

type Theme = "light" | "dark";

const THEME_EVENT = "app:theme-change";

function applyTheme(t: Theme) {
  const root = document.documentElement;
  localStorage.setItem("theme", t);
  root.classList.toggle("dark", t === "dark");
  (root as any).style.colorScheme = t;
  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: t }));
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => "dark");

  // Sync state from DOM/localStorage on mount, and listen for cross-instance changes
  useEffect(() => {
    const read = (): Theme => {
      try {
        return (localStorage.getItem("theme") as Theme | null) || "dark";
      } catch { return "dark"; }
    };
    setThemeState(read());

    const handler = (e: Event) => {
      setThemeState((e as CustomEvent<Theme>).detail);
    };
    window.addEventListener(THEME_EVENT, handler);
    return () => window.removeEventListener(THEME_EVENT, handler);
  }, []);

  function set(t: Theme) {
    try {
      applyTheme(t);
      setThemeState(t);
    } catch {}
  }

  function toggle() {
    set(theme === "dark" ? "light" : "dark");
  }

  function system() {
    try {
      localStorage.removeItem("theme");
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      const t: Theme = prefersDark ? "dark" : "light";
      const root = document.documentElement;
      root.classList.toggle("dark", t === "dark");
      (root as any).style.colorScheme = t;
      window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: t }));
      setThemeState(t);
    } catch {}
  }

  return { theme, setTheme: set, toggle, system };
}

